import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Icon, Input, message } from 'antd';
import * as S from '../styles';
import { useBatchJob } from '../../../../../context/BatchJobContext';
import { AppStoreContext } from '../../../../../context/appStoreContext';
import { getConsolidateVariableContract } from '../../../../../utils/services/channelAPI';
import {
  VARIABLE_OBJECT_TYPES,
  VARIABLE_TYPE,
  ALL_VARIABLE_OBJECT_TYPES,
} from '../../../../../utils/constants/appConstants';
import { dummyCallback } from '../../../../../utils/complexDocumentConstants';
import { getTodaysDateWithFormat, saveByteArray } from '../../../../../utils/javascript';
import VariableForm from '../../../../ComplexDocumentDesign/complexVariables/VariableForm';
import EmptyList from '../../../../../images/empty-list.svg';
import { ListContainer } from '../../../../ComplexDocumentDesign/complexVariables/styles';
import { EmptyListImage } from '../../../../../artboard/VariablesList/styles';
import styled from 'styled-components';
import { initiateTransaction } from '../../../../../utils/services/documentAPI';
import {
  getJsonFromArraybuffer,
  handleDataValidationErrors,
} from '../../../../../utils/dataValidationHelper';
import { useTranslation } from 'react-i18next';
import { COMMUNICATION_GENERATOR } from '../../../../TemplateSection/translationValues';
import { BATCH_JOBS_DASHBOARD as trans } from '../translationValues';
import { LoaderContainer } from '../../../../TemplateSection/styles';
import Loader from '../../../../../components/Loader';

const VariablesContainer = styled.div`
  width: 100%;
  > div > div {
    width: 100%;
  }
  > div > div > div {
    display: block;
    .variable-type {
      display: none;
      & + p {
        color: black;
      }
    }

    .ant-input {
      background: #fff !important;
      color: rgba(0, 0, 0, 0.65) !important;
      height: 44px;
      box-shadow: none;
      &:hover {
        border: 1px solid #e5e7eb !important;
      }
      &:focus {
        border: 2px solid #efb100 !important;
      }
    }
    .ant-btn-link {
      color: #000000;
      > i {
        color: inherit;
      }
      > span {
        color: inherit;
      }
    }
    .ant-calendar-picker {
      .ant-calendar-picker-icon {
        color: #000000;
      }
    }
    .ant-table-wrapper {
      margin-top: 0px !important;
      thead {
        tr {
          th {
            background: #fafafa;
            color: rgba(0, 0, 0, 0.85);
          }
        }
      }
      tbody {
        tr {
          td {
            background-color: #fff !important;
            color: rgba(0, 0, 0, 0.85) !important;
            .ant-input {
              color: rgba(0, 0, 0, 0.65) !important;
              background: #fff !important;
            }
          }
        }
      }
    }
    a {
      color: #000000;
    }
    .ant-collapse {
      .ant-collapse-item {
        background-color: #e9e9e9 !important;
        margin-bottom: 0px !important;
        .ant-collapse-header {
          color: #000000 !important;
        }
        .ant-collapse-content {
          .block {
            row-gap: 15px;
            padding-bottom: 0px;
            padding-top: 0px;
            .varName {
              margin-bottom: 10px;
              p {
                color: #000000;
                text-transform: none;
              }
              & + div {
                width: 100% !important;
              }
            }
            > div {
              display: block;
            }
          }
        }
      }
    }
    [id^='tool-'] {
      width: 100%;
      > div {
        width: 100%;
      }
      & > div > {
        .ant-collapse {
          > .ant-collapse-item {
            margin-bottom: 10px !important;
            > .ant-collapse-content > .ant-collapse-content-box > .block {
              padding-bottom: 20px;
            }
          }
        }
      }
    }
    .ql-toolbar.ql-snow {
      background: rgba(0, 0, 0, 0.8);
    }
    .ql-container.ql-snow {
      .ql-editor {
        & > p {
          color: #000000;
        }
      }
    }
  }
`;

const EmptyForm = styled.div`
  display: flex;
  height: 40vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const NAMESPACE = ['COMMUNICATION_GENERATOR', 'BATCH_JOBS_DASHBOARD'];

const Step3FormFilling = forwardRef((props, ref) => {
  const {
    format,
    formData,
    setFormData,
    toEmails,
    ccEmails,
    toEmailInput,
    setToEmailInput,
    ccEmailInput,
    setCcEmailInput,
    handleAddToEmail,
    handleRemoveToEmail,
    handleAddCcEmail,
    handleRemoveCcEmail,
    toPhoneNumbers,
    toPhoneNumberInput,
    setToPhoneNumberInput,
    handleAddToPhoneNumber,
    handleRemoveToPhoneNumber,
    selectedTemplate,
    selectedLanguage,
    jobType,
  } = useBatchJob();

  const { t } = useTranslation(NAMESPACE);

  const { globalVariables } = useContext(AppStoreContext);

  const [formVariables, setFormVariables] = useState([]);
  const [examplValuesData, setExamplValuesData] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [communicationName, setCommunicationName] = useState('');

  const successMessageMap = {
    PRINT: t(trans.DOC_DOWNLOAD_SUCCESS.key, trans.DOC_DOWNLOAD_SUCCESS.value),
    EMAIL: t(trans.EMAIL_REQ_SUCCESS.key, trans.EMAIL_REQ_SUCCESS.value),
    SMS: t(trans.SMS_SENT_SUCCESS.key, trans.SMS_SENT_SUCCESS.value),
    CALL: t(trans.CALL_INITIATED_SUCCESS.key, trans.CALL_INITIATED_SUCCESS.value),
  };

  const smFailureMessageMap = {
    'PDS-E006': t(trans.ERR_UNEXPECTED.key, trans.ERR_UNEXPECTED.value),
    'PDS-E007': t(trans.ERR_VAR_VALIDATION.key, trans.ERR_VAR_VALIDATION.value),
    'PDS-E011': t(trans.ERR_TEMPLATE_NOT_FOUND.key, trans.ERR_TEMPLATE_NOT_FOUND.value),
  };

  const modifyContract = (variables, globalVariables, parentName = '') => {
    return variables.map(v => {
      v.tempName = v.name;
      if (v.variableId) {
        const variableDetails = globalVariables.find(g => g.id === v.variableId);
        return { ...variableDetails, isMandatory: v.isMandatory };
      }
      if (v.content && Array.isArray(v.content)) {
        if (v.type === 'object') {
          v.type = VARIABLE_TYPE.TABLE_OBJECT;
        }
        if (v.content?.length > 0) {
          if (v.type === VARIABLE_TYPE.TABLE_OBJECT) {
            const parName = parentName ? `${parentName}.${v.name}` : v.name;
            v.name = parName;
            v.variables = modifyContract(v.content, globalVariables, parName);
            v.content = undefined;
          } else {
            v.variables = modifyContract(v.content, globalVariables);
            v.content = undefined;
          }
        } else {
          v.variables = [];
          v.content = undefined;
        }
      }
      return { ...v, placeHolderText: v.tempName, tempName: undefined };
    });
  };

  const documentVariables = useMemo(() => {
    let docVariables = formVariables;
    if (globalVariables?.length) {
      docVariables = modifyContract(formVariables, globalVariables, undefined);
    }
    return docVariables;
  }, [formVariables, globalVariables]);

  const generateDataSet = (variable, parentDataSet = {}, sampleData = {}, nested = false) => {
    if (variable.type === VARIABLE_TYPE.OBJECT || variable.type === VARIABLE_TYPE.TABLE_OBJECT) {
      let isArray = nested ? variable.isArray || false : true;
      let listArray = [];
      let d = sampleData?.value?.forEach(eachBlock => {
        let blockSamples = {};
        eachBlock.value.forEach(block => {
          if (block.type === VARIABLE_TYPE.CHART_OBJECT) {
            blockSamples[block.name] = block.value.source;
          } else if (block.type === VARIABLE_TYPE.TABLE_OBJECT) {
            blockSamples = generateDataSet(
              variable.variables.find(v => v.placeHolderText === block.name),
              blockSamples,
              block.value,
              variable.type === VARIABLE_TYPE.OBJECT ? !variable.isArray : true,
            );
          } else {
            blockSamples[block.name] = block.value;
          }
        });
        listArray.push(blockSamples);
      });
      parentDataSet = {
        ...parentDataSet,
        [variable.placeHolderText]: isArray ? listArray : listArray[0],
      };
    } else if (
      variable.type === VARIABLE_TYPE.CHART_OBJECT ||
      variable.type === VARIABLE_TYPE.FORM_OBJECT
    ) {
      const listArray = [];
      sampleData &&
        sampleData.value &&
        sampleData.value.forEach(eachBlock => {
          let blockSamples = {};
          eachBlock.value.forEach(block => {
            blockSamples[block.name] = block.value.source;
          });
          listArray.push(blockSamples);
        });
      parentDataSet = {
        ...parentDataSet,
        ...listArray[0],
      };
    } else {
      parentDataSet = {
        ...parentDataSet,
        [variable.placeHolderText]: sampleData?.value || '',
      };
    }
    return parentDataSet;
  };

  const generateTransaction = async () => {
    let dataSet = {
      uniq_id: '',
      uniq_name: '',
    };
    documentVariables.forEach(variable => {
      const key = ALL_VARIABLE_OBJECT_TYPES.includes(variable.type)
        ? variable.name
        : variable.placeHolderText;
      dataSet = generateDataSet(variable, dataSet, examplValuesData[key]);
    });
    const payload = {
      communicationId: selectedTemplate,
      channel: format.toLowerCase(),
      language: selectedLanguage?.code,
      tagsInfo: [],
      dataSet: dataSet,
    };

    if (format === 'EMAIL') {
      payload.to = toEmails;
      payload.cc = ccEmails;
    } else if (format === 'SMS' || format === 'CALL') {
      payload.toNumber = toPhoneNumbers.join(',');
    } else {
      payload.printable = false;
    }
    // setShowDownloadLoader(true);
    try {
      const isShortMessage = format === 'SMS' || format === 'CALL';
      const response = await initiateTransaction(payload, isShortMessage);
      const res = getJsonFromArraybuffer(response);
      if (!res?.errorCode) {
        if (format === 'PRINT') {
          saveByteArray(communicationName || 'document', response.data);
        }
        message.success(successMessageMap[format]);
        // setShowDocument(true);
      } else {
        const errorMessages = handleDataValidationErrors(res.data);
        if (Object.keys(errorMessages)?.length > 0) {
          message.error(
            t(
              COMMUNICATION_GENERATOR.VALIDATION_ERROR_MESSAGE.key,
              COMMUNICATION_GENERATOR.VALIDATION_ERROR_MESSAGE.value,
            ),
          );
        } else {
          if (format === 'EMAIL') {
            message.error(
              t(
                COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.key,
                COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.value,
              ),
            );
          }
          if (format === 'PRINT') {
            message.error(
              t(
                COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.key,
                COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.value,
              ),
            );
          } else if (res?.errorCode) {
            message.error(smFailureMessageMap[res.errorCode] || smFailureMessageMap['PDS-E006']);
          }
        }
        setErrorMessages(handleDataValidationErrors(res.data));
      }
    } catch (e) {
      if (format === 'EMAIL') {
        message.error(
          t(
            COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.key,
            COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.value,
          ),
        );
      } else {
        message.error(
          t(
            COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.key,
            COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.value,
          ),
        );
      }
      console.error(e, 'Transaction initiation failed');
    } finally {
      // setShowDownloadLoader(false);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: generateTransaction,
  }));

  const constructChartVariable = (variables, isFormTable = false) => {
    const source = {};
    const header = [];
    variables.forEach(item => {
      header.push(item.placeHolderText);
      Object.keys(item).forEach(key => {
        if (key === 'placeHolderText') {
          if (isFormTable) {
            if (item.placeHolderText === 'selected') {
              source[item.placeHolderText] = 'false';
            } else {
              source[item.placeHolderText] = '';
            }
          } else {
            source[item.placeHolderText] = '';
          }
        }
      });
    });
    return { header, source: [source], isObject: true };
  };

  useEffect(() => {
    if (documentVariables?.length > 0 && globalVariables?.length > 0) {
      const getSampleData = (variables, parent = undefined) => {
        let sampleData = {};
        let newBlockName = `block1`;
        variables.forEach((element, index) => {
          if (element.type === 'ListString' || element.type === 'listOfStrings') {
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                key: element.placeHolderText,
                value: [],
              },
            };
          } else if (element.type === VARIABLE_TYPE.CHART_OBJECT) {
            const objArray = [];
            objArray.push({
              key: element.name,
              subKey: newBlockName,
              name: element.placeHolderText,
              type: element.type,
              isObject: true,
              value: constructChartVariable(element.variables),
              parent,
            });
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                value: [{ key: newBlockName, value: objArray }],
                type: element.type,
                key: element.name,
              },
            };
            // console.log("element name :: ", element.name, sampleData);
          } else if (element.type === VARIABLE_TYPE.FORM_OBJECT) {
            const objArray = [];
            objArray.push({
              key: element.name,
              subKey: newBlockName,
              name: element.placeHolderText,
              type: element.type,
              isObject: true,
              value: constructChartVariable(element.variables, true),
              parent,
            });
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                value: [{ key: newBlockName, value: objArray }],
                type: element.type,
                key: element.name,
              },
            };
          } else if (
            VARIABLE_OBJECT_TYPES.includes(element.type) ||
            element.type === VARIABLE_TYPE.TABLE_OBJECT
          ) {
            let objArray = [];
            element.variables.forEach(eachVar => {
              objArray.push({
                key: element.name,
                subKey: newBlockName,
                name: eachVar.placeHolderText,
                type: eachVar.type,
                isObject: true, // TODO : Continue from here to do CRUD operations
                parent,
                isMandatory: eachVar.isMandatory,
                value:
                  eachVar.type === 'Date'
                    ? getTodaysDateWithFormat()
                    : eachVar.type === 'ListString' || eachVar.type === 'listOfStrings'
                    ? []
                    : eachVar.type === VARIABLE_TYPE.CHART_OBJECT
                    ? constructChartVariable(eachVar.variables)
                    : eachVar.type === 'object' || eachVar.type === VARIABLE_TYPE.TABLE_OBJECT
                    ? getSampleData([eachVar], `${parent ? `${parent}.` : ''}${newBlockName}`)[
                        eachVar.placeHolderText
                      ]
                    : '',
                extraInfo: eachVar?.extraInfo,
                listInfo: eachVar?.listInfo,
                numberInfo: eachVar?.numberInfo,
                isArray: eachVar.isArray || false,
              });
            });
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                value: [{ key: newBlockName, value: objArray }],
                type: element.type,
                key: element.name,
              },
            };
          } else {
            sampleData = {
              ...sampleData,
              [element.placeHolderText]:
                element.type === 'Date'
                  ? { key: element.placeHolderText, value: getTodaysDateWithFormat() }
                  : { key: element.placeHolderText, value: '' },
            };
          }
        });
        return sampleData;
      };
      let designSampleData = getSampleData(documentVariables);
      setExamplValuesData(designSampleData);
    }
  }, [documentVariables]);

  const fetchConsolidatedVariables = async () => {
    setFormVariables([]);
    const payload = {
      communicationId: selectedTemplate,
      channel: format.toLowerCase(),
      language: selectedLanguage?.code,
    };
    // setShowLoader(true);
    try {
      const res = await getConsolidateVariableContract(payload, 'live');
      if (res.statusCode === 200 && res.data) {
        setFormVariables(res.data.variableContract);
        if (format === 'PRINT') {
          setCommunicationName(res.data?.communicationName);
        }
        return true;
      } else {
        message.error(t(trans.ERR_COMM_ID_NOT_EXIST.key, trans.ERR_COMM_ID_NOT_EXIST.value));
        return false;
      }
    } catch (e) {
      console.error(e);
      message.error(t(trans.ERR_COMM_ID_NOT_EXIST.key, trans.ERR_COMM_ID_NOT_EXIST.value));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConsolidatedVariables();
  }, []);

  const updateExamplesValue = value => {
    setExamplValuesData(value);
    setErrorMessages({});
  };

  const templateVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    if (format === 'EMAIL') {
      return ['toEmail', 'ccEmail'];
    } else if (format === 'SMS' || format === 'CALL') {
      return ['toPhoneNumber'];
    }
    return [];
  }, [selectedTemplate, format]);

  const fieldMap = {
    toEmail: t(trans.TO_EMAIL_ADDRESS.key, trans.TO_EMAIL_ADDRESS.value),
    ccEmail: t(trans.CC_EMAIL_ADDRESS.key, trans.CC_EMAIL_ADDRESS.value),
    toPhoneNumber: t(trans.TO_PHONE_NUMBER.key, trans.TO_PHONE_NUMBER.value),
  };

  const fieldDescriptionMap = {
    toEmail: t(trans.TO_EMAIL_DESCRIPTION.key, trans.TO_EMAIL_DESCRIPTION.value),
    ccEmail: t(trans.CC_EMAIL_DESCRIPTION.key, trans.CC_EMAIL_DESCRIPTION.value),
    toPhoneNumber: t(trans.PHONE_NUMBER_DESCRIPTION.key, trans.PHONE_NUMBER_DESCRIPTION.value),
  };

  return (
    <S.StepContent style={{ height: '100%' }}>
      <S.FormContainer style={{ height: '100%' }}>
        <S.StepHeader>
          <S.StepHeaderTitle>
            {t(trans.FILL_DYNAMIC_VALUES_TITLE.key, trans.FILL_DYNAMIC_VALUES_TITLE.value)}
          </S.StepHeaderTitle>
          <S.StepSubtitle>
            {t(trans.ENTER_INFO_FOR.key, trans.ENTER_INFO_FOR.value)}{' '}
            {(() => {
              const ch = format.toUpperCase();
              const channelKeyMap = {
                PRINT: trans.CHANNEL_PRINT,
                EMAIL: trans.CHANNEL_EMAIL,
                SMS: trans.CHANNEL_SMS,
                CALL: trans.CHANNEL_CALL,
                'MULTI CHANNEL': trans.CHANNEL_MULTICHANNEL,
                MULTICHANNEL: trans.CHANNEL_MULTICHANNEL,
              };
              const channelTrans = channelKeyMap[ch];
              return channelTrans ? t(channelTrans.key, channelTrans.value).toLowerCase() : format.toLowerCase();
            })()}
          </S.StepSubtitle>
        </S.StepHeader>
        {isLoading ? (
          <LoaderContainer style={{ height: '50%' }}>
            <Loader />
          </LoaderContainer>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {['EMAIL', 'SMS', 'CALL'].includes(format) && jobType === 'FORM' && (
              <div style={{ padding: '18px', border: '1px solid #bdbdbd', borderRadius: '12px' }}>
                {templateVariables.map(field => {
                  const isEmailTag = ['toEmail', 'ccEmail'].includes(field);
                  const isPhoneTag = field === 'toPhoneNumber';
                  return (
                    <div key={field}>
                      <S.Label style={{ marginBottom: '6px' }}>
                        {fieldMap[field]} {field !== 'ccEmail' && <S.Required>*</S.Required>}
                      </S.Label>
                      {format !== 'PRINT' && (
                        <p style={{ fontSize: '12px', marginBottom: '6px' }}>
                          {fieldDescriptionMap[field]}
                        </p>
                      )}
                      {isPhoneTag ? (
                        <S.EmailAddresses>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {toPhoneNumbers.map(p => (
                              <S.StyledTag key={p}>
                                {p}{' '}
                                <Icon type="close" onClick={() => handleRemoveToPhoneNumber(p)} />
                              </S.StyledTag>
                            ))}
                          </div>
                          <Input
                            value={toPhoneNumberInput}
                            onChange={e => setToPhoneNumberInput(e.target.value)}
                            onKeyDown={e =>
                              e.key === 'Enter' && handleAddToPhoneNumber(toPhoneNumberInput)
                            }
                            placeholder={t(
                              trans.PHONE_NUMBER_PLACEHOLDER.key,
                              trans.PHONE_NUMBER_PLACEHOLDER.value,
                            )}
                            style={{ border: 'none', padding: 0 }}
                          />
                        </S.EmailAddresses>
                      ) : isEmailTag ? (
                        <S.EmailAddresses>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {(field === 'toEmail' ? toEmails : ccEmails).map(e => (
                              <S.StyledTag key={e}>
                                {e}{' '}
                                <Icon
                                  type="close"
                                  onClick={() =>
                                    field === 'toEmail'
                                      ? handleRemoveToEmail(e)
                                      : handleRemoveCcEmail(e)
                                  }
                                />
                              </S.StyledTag>
                            ))}
                          </div>
                          <Input
                            value={field === 'toEmail' ? toEmailInput : ccEmailInput}
                            onChange={e =>
                              field === 'toEmail'
                                ? setToEmailInput(e.target.value)
                                : setCcEmailInput(e.target.value)
                            }
                            onKeyDown={e =>
                              e.key === 'Enter' &&
                              (field === 'toEmail'
                                ? handleAddToEmail(e.target.value)
                                : handleAddCcEmail(e.target.value))
                            }
                            placeholder={
                              field === 'toEmail'
                                ? t(
                                    trans.TO_EMAIL_PLACEHOLDER.key,
                                    trans.TO_EMAIL_PLACEHOLDER.value,
                                  )
                                : t(
                                    trans.CC_EMAIL_PLACEHOLDER.key,
                                    trans.CC_EMAIL_PLACEHOLDER.value,
                                  )
                            }
                            style={{ border: 'none', padding: 0, boxShadow: 'none' }}
                          />
                        </S.EmailAddresses>
                      ) : (
                        <Input
                          value={formData[field]}
                          onChange={e => setFormData({ ...formData, [field]: e.target.value })}
                          size="large"
                          style={{ borderRadius: '12px' }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {formVariables?.length > 0 ? (
              <VariablesContainer>
                <ListContainer>
                  <VariableForm
                    documentVariables={documentVariables}
                    examplValuesData={examplValuesData}
                    setExamplValuesData={updateExamplesValue}
                    errorMessages={errorMessages}
                    isHtmlExpanded={false}
                    setIsHtmlExpanded={dummyCallback}
                    isInteractiveDesign={false}
                    isGenerateFormPage={true}
                  />
                </ListContainer>
              </VariablesContainer>
            ) : (
              <EmptyForm>
                <EmptyListImage>
                  <img src={EmptyList} alt="empty-list" />
                </EmptyListImage>
                {t(trans.NO_VARIABLES.key, trans.NO_VARIABLES.value)}
              </EmptyForm>
            )}
          </div>
        )}
      </S.FormContainer>
    </S.StepContent>
  );
});

export default Step3FormFilling;
